import { useState, useEffect, useMemo } from "react";
import API from "../../api";
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Header from "../../components/Header";
import { StandardGridToolbar } from "../../components/ToolbarKit";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { dataGridStyle, palette } from "../../theme";
import dayjs from "dayjs";
import { MUTED } from "../dashboard/dashboard-components/resellerUi";

const FILTERS = [
  { key: "30", label: "Expires in 30 d" },
  { key: "60", label: "Expires in 60 d" },
  { key: "active", label: "Active" },
  { key: "expired", label: "Expired" },
  { key: "all", label: "All" },
];

const safeDaysLeft = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};

// Stessa semantica dei filtri originali
const matchesFilter = (row, mode) => {
  const days = safeDaysLeft(row?.giorniRimanenti);
  if (days === null) return mode === "all";
  if (mode === "30") return days <= 30 && days > 0;
  if (mode === "60") return days <= 60 && days > 0;
  if (mode === "active") return days > 0;
  if (mode === "expired") return days <= 0;
  return true;
};

const formatDate = (v) => {
  if (!v) return "-";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY") : "-";
};

const Muted = () => (
  <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
    -
  </Typography>
);

const textCell = (params) => (params.value ? params.value : <Muted />);

const columns = [
  { field: "ragioneSociale", headerName: "Name", flex: 1, minWidth: 160, renderCell: textCell },
  { field: "email", headerName: "Email", flex: 1, minWidth: 200, renderCell: textCell },
  { field: "matricola", headerName: "Serial number", width: 140, renderCell: textCell },
  {
    field: "giorniRimanenti",
    headerName: "Days left",
    width: 110,
    renderCell: (params) => {
      const v = params.value;
      if (v !== 0 && !v) return <Muted />;
      const n = Number(v);
      const color = !Number.isFinite(n)
        ? undefined
        : n <= 0
          ? palette.error.light
          : n <= 30
            ? "#EF9F27"
            : undefined;
      return (
        <Typography component="span" sx={{ fontSize: 13, color }}>
          {v}
        </Typography>
      );
    },
  },
  { field: "nomeLecs", headerName: "LECS name", flex: 1, minWidth: 140, renderCell: textCell },
  { field: "codiceAttivazione", headerName: "Activation code", width: 170, renderCell: textCell },
  {
    field: "tipoLicenza",
    headerName: "License type",
    width: 130,
    renderCell: (params) => {
      if (Number(params.value)) return `${params.value} Year`;
      if (params.value) return params.value;
      return <Muted />;
    },
  },
  {
    field: "dataAttivazione",
    headerName: "Activation",
    width: 120,
    renderCell: (params) => formatDate(params.value),
  },
  {
    field: "dataScadenza",
    headerName: "Expiration at",
    width: 130,
    renderCell: (params) => formatDate(params.value),
  },
];

const LicensesManagement = () => {
  const [allData, setAllData] = useState([]);
  const [filterMode, setFilterMode] = useState("60");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach(({ key }) => {
      c[key] = allData.filter((r) => matchesFilter(r, key)).length;
    });
    return c;
  }, [allData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allData.filter((row) => {
      if (!matchesFilter(row, filterMode)) return false;
      if (!q) return true;
      return [row?.ragioneSociale, row?.email, row?.matricola, row?.nomeLecs, row?.codiceAttivazione].some(
        (v) => String(v ?? "").toLowerCase().includes(q)
      );
    });
  }, [allData, filterMode, search]);

  useEffect(() => {
    API.getCustomersLicenses()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const newData = list.map((item, index) => ({
          id: index + 1,
          ...item,
        }));
        setAllData(newData);
      })
      .catch((err) => {});
  }, []);

  const toolbar = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        placeholder="Search name, email, serial, LECS…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {FILTERS.map(({ key, label }) => {
          const active = filterMode === key;
          return (
            <Chip
              key={key}
              size="small"
              clickable
              onClick={() => setFilterMode(key)}
              label={`${label} · ${counts[key]}`}
              sx={{
                color: active ? "#eaeaea" : MUTED,
                backgroundColor: active ? "rgba(69,144,149,0.18)" : "transparent",
                border: "1px solid",
                borderColor: active ? palette.primary.main : "rgba(255,255,255,0.14)",
                "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <Box m={"20px"}>
      <Header title={"License Management"} />

      <Box mt={2} mb={2}>{toolbar}</Box>
      <Box sx={{ height: "calc(100vh - 290px)", minHeight: 460 }}>
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          slots={{ toolbar: StandardGridToolbar }}
          showToolbar
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: "No licenses found" }}
          sx={dataGridStyle}
        />
      </Box>
    </Box>
  );
};

export default LicensesManagement;
